import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../firebase/firebase";
import {
  doc,
  getDoc,
  setDoc,
  increment,
  serverTimestamp
} from "firebase/firestore";
import { logActivity } from "../../utils/analyticsActivity";
import { loadProductAnalytics } from "../../utils/loadProductAnalytics";
import Banner from "../../images/banner.jpg";
import "./ProductPage.css";
import Footer from "../Footer/Footer";
import LikeButton from "./LikeButton";
import NavBar from "../Layout/NavBar";


const ProductPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState("next");
  const [animating, setAnimating] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  // Load product
  useEffect(() => {
    async function loadProduct() {
      const ref = doc(db, "products", productId);
      const snap = await getDoc(ref);

      if (snap.exists()) {
        setProduct({ id: snap.id, ...snap.data() });
      }
    }

    loadProduct();
  }, [productId]);

  // Track product view
  useEffect(() => {
    if (!product) return;

    const trackView = async () => {
      const pid = product.id;
      const today = new Date().toISOString().slice(0, 10);

      await setDoc(
        doc(db, "analyticsProducts", pid),
        {
          views: increment(1),
          lastViewed: serverTimestamp(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "analyticsDaily", today),
        {
          date: today,
          totalViews: increment(1),
        },
        { merge: true }
      );
    };

    trackView();
  }, [product]);

  // Activity feed
  useEffect(() => {
    if (product) logActivity("view", product.id);
  }, [product]);

  useEffect(() => {
    async function load() {
      const merged = await loadProductAnalytics(30);

      const product = merged.find((p) => p.id === productId);

      setProduct(product);
    }
    load();
  }, [productId]);

  // Real-time analytics updates (likes, views, etc)
  useEffect(() => {
    if (!productId) return;

    // Prevent multiple view counts per session
    const viewKey = `viewed_product_${productId}`;
    if (sessionStorage.getItem(viewKey)) return;
    sessionStorage.setItem(viewKey, "true");

    const today = new Date().toISOString().slice(0, 10);

    // Increment view once
    setDoc(
      doc(db, "analyticsProducts", productId),
      {
        views: increment(1),
        lastViewed: serverTimestamp(),
      },
      { merge: true }
    );

    setDoc(
      doc(db, "analyticsDaily", today),
      {
        date: today,
        totalViews: increment(1),
      },
      { merge: true }
    );
  }, [productId]);



  // Amazon click tracking
  const handleAmazonClick = async () => {
    if (!product) return;

    const pid = product.id;
    const today = new Date().toISOString().slice(0, 10);

    await setDoc(
      doc(db, "analyticsProducts", pid),
      {
        amazonClicks: increment(1),
        lastClicked: serverTimestamp(),
      },
      { merge: true }
    );

    await setDoc(
      doc(db, "analyticsDaily", today),
      {
        date: today,
        totalClicks: increment(1),
      },
      { merge: true }
    );

    logActivity("click", pid);
  };

  if (!product) return <div className="loading">Loading...</div>;

  // Build image list
  const images = [
    product.image,
    product.image2,
    ...(product.examples || []),
  ].filter(Boolean);

  const nextImage = () => {
    if (animating) return;
    setDirection("next");
    setAnimating(true);

    setTimeout(() => {
      setCurrentIndex((prev) =>
        prev === images.length - 1 ? 0 : prev + 1
      );
      setAnimating(false);
    }, 300);
  };

  const prevImage = () => {
    if (animating) return;
    setDirection("prev");
    setAnimating(true);

    setTimeout(() => {
      setCurrentIndex((prev) =>
        prev === 0 ? images.length - 1 : prev - 1
      );
      setAnimating(false);
    }, 300);
  };

  const handleTouchStart = (e) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;

    const distance = touchStartX - touchEndX;

    if (Math.abs(distance) < 50) {
      setTouchStartX(null);
      setTouchEndX(null);
      return;
    }

    if (distance > 0) nextImage();
    else prevImage();

    setTouchStartX(null);
    setTouchEndX(null);
  };

  function timeAgo(ts) {
    const date = ts.toDate();
    const diff = (Date.now() - date.getTime()) / 1000; // seconds

    const minutes = diff / 60;
    const hours = diff / 3600;
    const days = diff / 86400;
    const months = diff / (86400 * 30);   // approx
    const years = diff / (86400 * 365);   // approx

    if (diff < 60) return "Just now";
    if (minutes < 60) return `${Math.floor(minutes)}m ago`;
    if (hours < 24) return `${Math.floor(hours)}h ago`;
    if (days < 30) return `${Math.floor(days)}d ago`;
    if (months < 2) return `${Math.floor(months)}mo ago`;
    if (months < 12) return `${Math.floor(months)}mos ago`;
    return `${Math.floor(years)}y ago`;
  }

  return (
    <div className="product-page">
      <div className="product-banner">
        <img src={Banner} alt="Shop Banner" />
      </div>

      <NavBar />

      <div className="product-main">
        {/* IMAGE CAROUSEL */}
        <div
          className="carousel-container"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <button className="carousel-arrow left" onClick={prevImage}>
            ❮
          </button>

          <div className={`carousel-slide ${animating ? direction : ""}`}>
            <img
              src={images[currentIndex]}
              alt={product.title}
              className="carousel-image"
            />
          </div>

          <button className="carousel-arrow right" onClick={nextImage}>
            ❯
          </button>

          <div className="carousel-dots">
            {images.map((_, index) => (
              <div
                key={index}
                className={`dot ${index === currentIndex ? "active" : ""}`}
                onClick={() => setCurrentIndex(index)}
              ></div>
            ))}
          </div>
        </div>

        {/* PRODUCT DETAILS */}
        <div className="product-details">
          <h1>{product.title}</h1>

          {/* Tags */}
          {product.tags?.length > 0 && (
            <div className="product-tags">
              {product.tags.slice(0, 3).map((tag, i) => (
                <span key={i} className="product-tag">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Description */}
          <p className="product-description">{product.description}</p>

          {/* Buy Button */}
          <div className="buy-button-wrapper">
            <a
              href={product.amazonLink}
              target="_blank"
              rel="noopener noreferrer"
              className="buy-button"
              onClick={handleAmazonClick}
            >
              Buy on Amazon
              - ${product.price}
            </a>

                      {/* Price */}
         
          </div>

          {/* Optional analytics */}
          <div className="product-meta">
            <div className="product-meta-row">
              {product.views !== undefined && (
                <p className="eye">👁️ {product.views} views</p>
              )}
              {product.likes !== undefined && (
                <LikeButton
                  id={product.id}
                  type="product"
                  initialLikes={product.likes}
                />
              )}
            </div>

            <div className="product-footer">
              <span className="product-date">{timeAgo(product.createdAt)}</span>
            </div>
          </div>


        </div>
      </div>

      {/* GALLERY */}
      {product.examples?.length > 0 && (
        <div className="product-gallery">
          <h2>Inside the Book</h2>

          <div className="gallery-grid">
            {product.examples.map((img, index) => (
              <img key={index} src={img} alt="Example page" />
            ))}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductPage;
